import React, { useMemo } from "react";
import { MoveLeft, MoveRight } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "ui/ToggleGroup";
import { cn } from "util/cn";
import { TEABATE_ALIGNMENT_DIMENSIONS } from "components/Posts/kindFields/TeabateFields";

export interface AlignmentScaleOption {
  value: number;
  label: string;
}

interface AlignmentFormContentProps {
  teabateTitle?: string | null;
  dimension?: string | null;
  response: number | null;
  importance: number | null;
  onResponseChange: (value: number) => void;
  onImportanceChange: (value: number) => void;
  showTeabateTitle?: boolean;
  claimLabel?: string;
  responseLabel?: string;
  importanceLabel?: string;
  responseOptions?: AlignmentScaleOption[];
  importanceOptions?: AlignmentScaleOption[];
  className?: string;
}

const DEFAULT_RESPONSE_OPTIONS: AlignmentScaleOption[] = [
  { value: -2, label: "Strongly Disagree" },
  { value: -1, label: "Disagree" },
  { value: 0, label: "Neutral" },
  { value: 1, label: "Agree" },
  { value: 2, label: "Strongly Agree" },
];

const DEFAULT_IMPORTANCE_OPTIONS: AlignmentScaleOption[] = [
  { value: -2, label: "Very Unimportant" },
  { value: -1, label: "Unimportant" },
  { value: 0, label: "Neutral" },
  { value: 1, label: "Important" },
  { value: 2, label: "Very Important" },
];

/**
 * Shared claim + alignment + importance fields used by the standard alignment
 * modal and the quiz-specific response flow.
 */
const AlignmentFormContent: React.FC<AlignmentFormContentProps> = ({
  teabateTitle = null,
  dimension = null,
  response,
  importance,
  onResponseChange,
  onImportanceChange,
  showTeabateTitle = true,
  claimLabel = "Claim",
  responseLabel = "Alignment",
  importanceLabel = "Importance",
  responseOptions = DEFAULT_RESPONSE_OPTIONS,
  importanceOptions = DEFAULT_IMPORTANCE_OPTIONS,
  className = "",
}) => {
  const dimensionLabels = useMemo(() => {
    const normalizedDimension = String(dimension || "").trim().toLowerCase();
    if (!normalizedDimension) {
      return null;
    }

    const category = TEABATE_ALIGNMENT_DIMENSIONS.find((group) =>
      group.label1.toLowerCase() === normalizedDimension || group.label2.toLowerCase() === normalizedDimension
    );

    if (!category) {
      return null;
    }

    const isLabel1 = category.label1.toLowerCase() === normalizedDimension;
    return {
      disagreeLabel: isLabel1 ? category.label2 : category.label1,
      agreeLabel: isLabel1 ? category.label1 : category.label2,
    };
  }, [dimension]);

  return (
    <div className={`space-y-6 ${className}`}>
      {showTeabateTitle && teabateTitle ? (
        <div>
          <label className="block text-sm font-semibold mb-1">
            {claimLabel}
          </label>
          <p className="p-3 bg-eoe-icon dark:bg-eoe-dark-icon rounded-xl text-sm text-eoe-text dark:text-eoe-dark-text">
            {teabateTitle}
          </p>
        </div>
      ) : null}

      <div className="mt-4">
        <div className="relative flex justify-center items-center mb-1">
          {dimensionLabels ? (
            <>
              <div className="absolute left-1 flex items-center gap-1 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                <MoveLeft className="size-3" />
                <span>{dimensionLabels.disagreeLabel}</span>
              </div>
              <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub font-semibold">
                {responseLabel}
              </span>
              <div className="absolute right-1 flex items-center gap-1 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                <span>{dimensionLabels.agreeLabel}</span>
                <MoveRight className="size-3" />
              </div>
            </>
          ) : (
            <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub font-semibold">
              {responseLabel}
            </p>
          )}
        </div>
        <ToggleGroup
          type="single"
          value={response !== null ? response.toString() : undefined}
          onValueChange={(value) => {
            if (!value) {
              return;
            }
            onResponseChange(Number.parseInt(value, 10));
          }}
          className="justify-between w-full [&>*:first-child]:rounded-l-2xl [&>*:last-child]:rounded-r-2xl"
        >
          {responseOptions.map((option) => (
            <ToggleGroupItem
              key={option.value}
              value={option.value.toString()}
              className={cn(
                "flex-1 text-[9px] sm:text-xxs md:text-xs font-light leading-snug data-[state=on]:text-[9px] bg-eoe-icon dark:bg-eoe-dark-icon rounded-none",
                response === option.value && "bg-eoe-blue dark:bg-eoe-blue hover:bg-eoe-blue dark:hover:bg-eoe-blue hover:text-white dark:hover:text-eoe-dark-text text-white dark:text-eoe-dark-text"
              )}
              aria-label={option.label}
            >
              {option.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div className="mt-4">
        <div className="flex justify-center items-center mb-1">
          <p className="text-xs font-semibold text-center text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {importanceLabel}
          </p>
        </div>
        <ToggleGroup
          type="single"
          value={importance !== null ? importance.toString() : undefined}
          onValueChange={(value) => {
            if (!value) {
              return;
            }
            onImportanceChange(Number.parseInt(value, 10));
          }}
          className="justify-between w-full [&>*:first-child]:rounded-l-2xl [&>*:last-child]:rounded-r-2xl"
        >
          {importanceOptions.map((option) => (
            <ToggleGroupItem
              key={option.value}
              value={option.value.toString()}
              className={cn(
                "flex-1 text-[9px] sm:text-xxs md:text-xs font-light leading-snug data-[state=on]:text-[9px] bg-eoe-icon dark:bg-eoe-dark-icon rounded-none",
                importance === option.value && "bg-eoe-blue dark:bg-eoe-blue hover:bg-eoe-blue dark:hover:bg-eoe-blue hover:text-white dark:hover:text-eoe-dark-text text-white dark:text-eoe-dark-text"
              )}
              aria-label={option.label}
            >
              {option.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
    </div>
  );
};

export default AlignmentFormContent;
